import React, { useLayoutEffect, useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { PlanBuilderProvider, usePlanBuilder } from './context/PlanBuilderContext';
import ConversationPanel from './ConversationPanel';
import ModuleCelebration from './ModuleCelebration';
import PlanBuilderHeader from './PlanBuilderHeader';
import LearningResourcesPanel from './panels/LearningResourcesPanel';
import ViewPlanPanel from './panels/ViewPlanPanel';
import PlanCompletionCelebration from './pages/PlanCompletionCelebration';
import PauseSchedulingDialog from './dialogs/PauseSchedulingDialog';
import ConfettiEffect from './effects/ConfettiEffect';
import GuidedTour from '../Shared/GuidedTour/GuidedTour';
import { XIcon, DownloadIcon, PartyPopperIcon, FileTextIcon, BookOpenIcon } from 'lucide-react';
import Button from '../../../../ui/canon/Button';

const PlanBuilderContent = ({ onSetCelebrationCallback }) => {
  const intl = useIntl();
  const { loading, error, progress, triggerConfetti, pausePlan, isPlanComplete, planTemplateId } = usePlanBuilder();
  const [contentHeight, setContentHeight] = useState('100vh');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const containerRef = useRef(null);
  
  // Panel State
  const [canvasView, setCanvasView] = useState('plan'); // 'resources' | 'plan' | 'celebration' | null
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showTour, setShowTour] = useState(false);
  
  // Handle plan completion - switch to celebration view
  const handlePlanComplete = () => {
    setCanvasView('celebration');
  };
  
  // Register the celebration callback with parent so context can trigger it
  useEffect(() => {
    if (onSetCelebrationCallback) {
      onSetCelebrationCallback(handlePlanComplete);
    }
  }, [onSetCelebrationCallback]);
  
  // Auto-switch to celebration view when plan is complete
  useEffect(() => {
    if (isPlanComplete && canvasView !== 'celebration') {
      setCanvasView('celebration');
    }
  }, [isPlanComplete]);

  const questionsAnswered = progress?.userPlanResponses?.length || 0;
  
  // Show guided tour for new plans (no responses yet)
  useEffect(() => {
    const isNewPlan = questionsAnswered === 0;
    const tourKey = `plan_builder_tour_shown_${planTemplateId}`;
    const hasSeenTour = localStorage.getItem(tourKey);
    
    if (isNewPlan && !hasSeenTour && !loading) {
      // Small delay to let UI render
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [questionsAnswered, planTemplateId, loading]);

  const handleTourComplete = () => {
    localStorage.setItem(`plan_builder_tour_shown_${planTemplateId}`, 'true');
    setShowTour(false);
  };

  const handleTourClose = () => {
    localStorage.setItem(`plan_builder_tour_shown_${planTemplateId}`, 'true');
    setShowTour(false);
  };

  const tourSteps = [
    {
      id: 'question-widget',
      title: intl.formatMessage({ id: 'succession.plans.tour_questions_title' }),
      description: intl.formatMessage({ id: 'succession.plans.tour_questions_description' }),
      targetSelector: '[data-tour="question-widget"]',
      position: 'right'
    },
    {
      id: 'ask-input',
      title: intl.formatMessage({ id: 'succession.plans.tour_ask_title' }),
      description: intl.formatMessage({ id: 'succession.plans.tour_ask_description' }),
      targetSelector: '[data-tour="ask-input"]',
      position: 'top'
    },
    {
      id: 'resources',
      title: intl.formatMessage({ id: 'succession.plans.tour_resources_title' }),
      description: intl.formatMessage({ id: 'succession.plans.tour_resources_description' }),
      targetSelector: '[data-tour="learning-resources"]',
      position: 'left'
    },
    {
      id: 'view-plan',
      title: intl.formatMessage({ id: 'succession.plans.tour_preview_title' }),
      description: intl.formatMessage({ id: 'succession.plans.tour_preview_description' }),
      targetSelector: '[data-tour="view-plan"]',
      position: 'left'
    }
  ];

  useLayoutEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Calculate available height by subtracting the top position from window height
        // This accounts for any header/nav that might be above this component (like AssetMark header)
        const height = Math.max(window.innerHeight - rect.top, 0);
        setContentHeight(`${height}px`);
      }
      setIsMobile(window.innerWidth < 1024);
    };

    // Prevent body scrolling while plan builder is active
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Initial calculation
    calculateHeight();

    // Recalculate on resize and scroll (in case of layout shifts)
    window.addEventListener('resize', calculateHeight);
    window.addEventListener('scroll', calculateHeight);

    // Use ResizeObserver to detect size changes in the body (e.g. header loading) which affects rect.top
    const resizeObserver = new ResizeObserver(() => {
      // Wrap in requestAnimationFrame to avoid "ResizeObserver loop completed with undelivered notifications" error
      window.requestAnimationFrame(() => {
        calculateHeight();
      });
    });
    resizeObserver.observe(document.body);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('resize', calculateHeight);
      window.removeEventListener('scroll', calculateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  if (loading) return <div className="p-8 text-center">{intl.formatMessage({ id: 'succession.plans.loading_plan_builder' })}</div>;
  if (error) return <div className="p-8 text-center text-red-500">{intl.formatMessage({ id: 'succession.plans.error_loading_plan_builder' }, { message: error.message })}</div>;

  const handlePauseClick = () => setShowPauseDialog(true);
  
  const handleSchedulePause = async (schedule) => {
    // Combine date and time into ISO8601 datetime (constructed in local time, then converted to UTC)
    const { date, time } = schedule;
    let scheduledResumeAt = null;
    
    if (date && time) {
      const [hours, minutes] = time.split(':').map(Number);
      const [year, month, day] = date.split('-').map(Number);
      // new Date(y, m-1, d, h, m) constructs local time deterministically (avoids YYYY-MM-DD UTC parsing quirks)
      const resumeDateLocal = new Date(year, month - 1, day, hours, minutes, 0, 0);
      scheduledResumeAt = resumeDateLocal.toISOString();
    }
    
    const result = await pausePlan(scheduledResumeAt);
    
    if (result.success) {
      console.log("Plan paused successfully, scheduled for:", scheduledResumeAt);
      // The dialog will handle closing and redirecting
    } else {
      console.error("Failed to pause plan:", result.errors);
    }

    // Let the dialog decide success vs error UI
    return result;
  };
  const handleViewResources = () => setCanvasView('resources');
  const handleViewPlan = () => setCanvasView('plan');
  const handleViewResults = () => setCanvasView('celebration');
  const handleCloseCanvas = () => setCanvasView(null);
  const handleExportPDF = () => alert("PDF Export coming soon");

  const hasResponses = questionsAnswered > 0;
  const resourceCount = progress?.planTemplate?.planModules?.reduce((acc, mod) => {
    return acc + (mod.planSections?.reduce((sAcc, sec) => {
      return sAcc + (sec.planQuestions?.reduce((qAcc, q) => qAcc + (q.educationalContents?.length || 0), 0) || 0);
    }, 0) || 0);
  }, 0) || 0;

  const showCanvas = canvasView !== null;

  // Get header text based on canvas view
  const getCanvasHeaderText = () => {
    switch (canvasView) {
      case 'resources':
        return { title: intl.formatMessage({ id: 'succession.plans.canvas_resources_title' }), subtitle: intl.formatMessage({ id: 'succession.plans.canvas_resources_subtitle' }) };
      case 'celebration':
        return { title: intl.formatMessage({ id: 'succession.plans.canvas_celebration_title' }), subtitle: intl.formatMessage({ id: 'succession.plans.canvas_celebration_subtitle' }) };
      default:
        return { title: intl.formatMessage({ id: 'succession.plans.canvas_plan_title' }), subtitle: intl.formatMessage({ id: 'succession.plans.canvas_plan_subtitle' }, { percent: progress?.completionPercentage || 0 }) };
    }
  };

  // Canvas Header Component
  const CanvasHeader = () => {
    const { title, subtitle } = getCanvasHeaderText();
    
    return (
      <div className="border-b border-succession-border bg-succession-bg-secondary shrink-0">
        <div className="px-6 py-2 flex items-center justify-between min-h-[52px]">
          <div className="flex items-center gap-3">
            {/* Icon container matching PlanBuilderHeader for alignment */}
            <div className="h-8 w-8 rounded-full bg-succession-primary/10 flex items-center justify-center flex-shrink-0">
              {canvasView === 'celebration' && <PartyPopperIcon className="w-4 h-4 text-blue-500" />}
              {canvasView === 'plan' && <FileTextIcon className="w-4 h-4 text-succession-primary" />}
              {canvasView === 'resources' && <BookOpenIcon className="w-4 h-4 text-succession-primary" />}
            </div>
            <div className="flex flex-col justify-center">
              <span className="font-bold text-succession-text-primary leading-tight" style={{ fontSize: '15px' }}>
                {title}
              </span>
              <span className="text-succession-text-secondary leading-tight" style={{ fontSize: '11px' }}>
                {subtitle}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canvasView === 'plan' && hasResponses && (
              <Button variant="outline" size="sm" onClick={handleExportPDF} className="hidden sm:flex h-7 text-xs">
                <DownloadIcon className="w-3.5 h-3.5 mr-2" />
                {intl.formatMessage({ id: 'succession.plans.download_pdf' })}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleCloseCanvas} className="hover:bg-succession-bg-primary p-1 h-auto">
              <XIcon className="h-5 w-5 text-succession-text-secondary" />
            </Button>
          </div>
        </div>
        {/* Thin progress bar for plan view */}
        {canvasView === 'plan' && (
          <div className="w-full bg-succession-bg-primary h-1">
            <div
              className="h-1 bg-green-500 transition-all duration-500"
              style={{ width: `${progress?.completionPercentage || 0}%` }}
            />
          </div>
        )}
        {/* Full progress bar for celebration view */}
        {canvasView === 'celebration' && (
          <div className="w-full bg-succession-bg-primary h-1">
            <div className="h-1 bg-green-500 w-full" />
          </div>
        )}
      </div>
    );
  };

  // Dynamic Styles for Split View
  // When celebrating (plan complete), hide chat and show celebration at full width
  const isCelebrating = canvasView === 'celebration';
  
  // Determine chat panel width:
  // - Hidden (0%) when: mobile + canvas open, OR celebrating
  // - 45% when: desktop + canvas open (but not celebrating)
  // - 100% when: no canvas open
  const getChatPanelWidth = () => {
    if ((showCanvas && isMobile) || isCelebrating) return '0%';
    if (showCanvas && !isMobile) return '45%';
    return '100%';
  };
  
  // Determine canvas panel width:
  // - 100% when celebrating
  // - 55% otherwise (when canvas is open)
  const getCanvasPanelWidth = () => {
    if (isCelebrating) return '100%';
    return '55%';
  };
  
  const chatPanelStyle = {
    display: 'flex',
    width: getChatPanelWidth(),
    height: '100%',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    opacity: ((showCanvas && isMobile) || isCelebrating) ? 0 : 1,
  };

  const canvasPanelStyle = {
    width: getCanvasPanelWidth(),
    height: '100%',
    flexDirection: 'column',
  };

  return (
    <div
      id="plan-builder-root"
      ref={containerRef}
      className="flex flex-col w-full bg-succession-bg-primary relative"
      style={{
        height: contentHeight,
        maxHeight: contentHeight,
        overflow: 'hidden',
        WebkitOverflowScrolling: 'touch',
        position: 'relative'
      }}
    >
      <PlanBuilderHeader 
        progress={progress?.completionPercentage || 0}
        questionsAnswered={questionsAnswered}
        onPause={handlePauseClick}
        onViewResources={handleViewResources}
        onViewPlan={handleViewPlan}
        onViewResults={handleViewResults}
        hasResponses={hasResponses}
        hasResources={true} 
        resourcesCount={resourceCount}
        isPlanComplete={isPlanComplete}
        planType={progress?.planTemplate?.planType}
        assistantName={progress?.planTemplate?.assistantName || 'AI Planning Assistant'}
      />

      {/* Main Content Area - calc accounts for PlanBuilderHeader (~52px) */}
      <div
        className="flex relative overflow-hidden w-full"
        style={{ height: 'calc(100% - 52px)' }}
      >
        <ModuleCelebration />

        {/* Chat Panel - always rendered, width animates to 0 when hidden */}
        <div
          style={chatPanelStyle}
          className="relative bg-succession-bg-secondary transition-all duration-300 ease-in-out border-r border-succession-border"
        >
          <ConversationPanel
            agentWord={window.Company?.agent_word || 'Advisor'}
            onViewPlan={handleViewPlan}
            onPauseClick={handlePauseClick}
          />
        </div>

        {/* Canvas Panel (Desktop) - always rendered when canvas is open, width animates */}
        {showCanvas && !isMobile && (
          <div 
            style={canvasPanelStyle}
            className="flex bg-succession-bg-primary transition-all duration-300 ease-in-out"
          >
             <div className="flex flex-col w-full h-full">
               <CanvasHeader />
               <div className="flex-1 overflow-hidden flex flex-col">
                  {canvasView === 'resources' && <LearningResourcesPanel />}
                  {canvasView === 'plan' && <ViewPlanPanel />}
                  {canvasView === 'celebration' && (
                    <PlanCompletionCelebration 
                      planTemplateId={planTemplateId} 
                      onEditPlan={() => setCanvasView('plan')}
                    />
                  )}
               </div>
             </div>
          </div>
        )}

        {/* Canvas Panel (Mobile Overlay) - slides in from right */}
        {showCanvas && isMobile && (
          <div 
            className="absolute inset-0 z-50 flex flex-col h-full bg-succession-bg-primary transition-all duration-300 ease-in-out"
            style={{ 
              transform: showCanvas ? 'translateX(0)' : 'translateX(100%)',
              opacity: showCanvas ? 1 : 0
            }}
          >
             <CanvasHeader />
             <div className="flex-1 overflow-hidden flex flex-col">
                {canvasView === 'resources' && <LearningResourcesPanel />}
                {canvasView === 'plan' && <ViewPlanPanel />}
                {canvasView === 'celebration' && (
                  <PlanCompletionCelebration 
                    planTemplateId={planTemplateId} 
                    onEditPlan={() => setCanvasView('plan')}
                  />
                )}
             </div>
          </div>
        )}
      </div>

      <PauseSchedulingDialog
        open={showPauseDialog}
        onOpenChange={setShowPauseDialog}
        onSchedule={handleSchedulePause}
        currentProgress={progress?.completionPercentage || 0}
        questionsAnswered={questionsAnswered}
      />

      {/* Confetti Effect for Module Completion */}
      <ConfettiEffect trigger={triggerConfetti} />

      {/* Guided Tour for New Plans */}
      <GuidedTour
        isOpen={showTour}
        steps={tourSteps}
        onClose={handleTourClose}
        onComplete={handleTourComplete}
        onReplayRequest={() => setShowTour(true)}
      />
    </div>
  );
};

const PlanBuilderPage = (props) => {
  const { planTemplateId } = props;
  // Use a ref to store the celebration callback that PlanBuilderContent will set
  const celebrationCallbackRef = useRef(null);
  
  const handlePlanComplete = () => {
    // Call the celebration callback set by PlanBuilderContent
    if (celebrationCallbackRef.current) {
      celebrationCallbackRef.current();
    }
  };
  
  return (
    <PlanBuilderProvider planTemplateId={planTemplateId} onPlanComplete={handlePlanComplete}>
      <PlanBuilderContent 
        onSetCelebrationCallback={(cb) => { celebrationCallbackRef.current = cb; }} 
      />
    </PlanBuilderProvider>
  );
};

export { PlanBuilderPage as default };
